import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { triggerInstallPrompt, isStandaloneMode, isIos } from "@/lib/pwa";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Persistent "Install this as an app" button that sits in the Navbar right
 * cluster. Hides itself when:
 *  - the app is already running in standalone/PWA mode, OR
 *  - the platform can neither fire `beforeinstallprompt` (Android/Chrome/Edge)
 *    nor offer the iOS Add-to-Home-Screen manual path.
 *
 * On desktop Chrome/Edge/Android: clicking triggers the deferred prompt.
 * On iOS Safari: opens a small dialog with the Share → Add to Home Screen
 * instructions.
 */
export default function NavInstallButton() {
  const [installable, setInstallable] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [standalone, setStandalone] = useState(false);
  const iosDevice = isIos();

  useEffect(() => {
    if (typeof window === "undefined") return;
    setStandalone(isStandaloneMode());
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => setStandalone(true);
    window.addEventListener("summit-air:installable", onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("summit-air:installable", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Nothing to offer if already installed, or the browser hasn't fired the
  // deferred prompt AND we're not on iOS.
  if (standalone) return null;
  if (!installable && !iosDevice) return null;

  const onClick = async () => {
    if (iosDevice && !installable) {
      setShowIosHelp(true);
      return;
    }
    const outcome = await triggerInstallPrompt();
    if (outcome === "accepted") {
      toast.success("Installing SA…");
      setStandalone(true);
    } else if (outcome === "dismissed") {
      toast.info("Install cancelled — you can try again anytime from the nav bar.");
    } else if (outcome === "unavailable") {
      if (iosDevice) setShowIosHelp(true);
      else toast.info("Your browser doesn't offer an install prompt right now.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-summit-primary text-summit-primary hover:bg-summit-primary hover:text-white text-xs font-mono uppercase tracking-widest transition-colors"
        data-testid="nav-install-btn"
        title="Install SA as a standalone app"
      >
        <Download className="w-3.5 h-3.5" />
        Install app
      </button>
      {/* Compact icon-only version for narrow viewports */}
      <button
        type="button"
        onClick={onClick}
        className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-summit-primary text-summit-primary hover:bg-summit-primary hover:text-white transition-colors"
        data-testid="nav-install-btn-mobile"
        title="Install SA as a standalone app"
        aria-label="Install as app"
      >
        <Download className="w-4 h-4" />
      </button>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent className="max-w-md" data-testid="ios-install-help">
          <DialogHeader>
            <DialogTitle>Install SA on iOS</DialogTitle>
            <DialogDescription>
              Safari doesn&apos;t offer a one-click install like Chrome/Edge on desktop and Android. Add SA to your home screen in three taps:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm mt-2">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-summit-glacier text-summit-primary flex items-center justify-center font-mono text-xs">1</span>
              <div>Tap the <span className="font-mono">Share</span> icon (square with an up-arrow) at the bottom of Safari.</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-summit-glacier text-summit-primary flex items-center justify-center font-mono text-xs">2</span>
              <div>Scroll and tap <span className="font-mono">Add to Home Screen</span>.</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-summit-glacier text-summit-primary flex items-center justify-center font-mono text-xs">3</span>
              <div>Tap <span className="font-mono">Add</span> in the top-right corner — SA will appear as its own app icon.</div>
            </li>
          </ol>
          <DialogFooter>
            <Button onClick={() => setShowIosHelp(false)} className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="ios-install-help-close">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
